import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './CampusContributionPanel.css';

const CATEGORIES = [
  ['hostel', 'Hostel'], ['mess', 'Mess / dining'], ['academic_building', 'Academic building'],
  ['department', 'Department'], ['laboratory', 'Laboratory'], ['research_centre', 'Research centre'],
  ['administration', 'Administration / office'], ['residential', 'Residential area'], ['sports', 'Sports'],
  ['healthcare', 'Healthcare'], ['food', 'Food / café'], ['shop', 'Shop / service'],
  ['transport', 'Transport stop'], ['guest_house', 'Guest house'], ['religious', 'Place of worship'],
  ['facility', 'Other campus facility'], ['other', 'Other'],
];

export function getCampusVoterId() {
  const key = 'moving-mountains-campus-voter-v1';
  let value = window.localStorage.getItem(key);
  if (!value) {
    value = window.crypto?.randomUUID?.() || `anon-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(key, value);
  }
  return value;
}

function PointPreview({ point }) {
  if (!point) return null;
  return (
    <div className="contribution-point-preview">
      <span className="contribution-point-pulse" />
      <div>
        <strong>Location pinned</strong>
        <small>{Number(point.lat).toFixed(6)}, {Number(point.lng).toFixed(6)}</small>
      </div>
    </div>
  );
}

export default function CampusContributionPanel({
  open,
  mapPicking = false,
  backend,
  initialName,
  userLocation,
  pickedPoint,
  onBeginMapPick,
  onEndMapPick,
  onClose,
  onSubmitted,
}) {
  const [name, setName] = useState(initialName || '');
  const [category, setCategory] = useState('academic_building');
  const [aliases, setAliases] = useState('');
  const [description, setDescription] = useState('');
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [evidenceNote, setEvidenceNote] = useState('');
  const [point, setPoint] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    if (open) {
      setName(initialName || '');
      setError('');
      setSubmitted(null);
    }
  }, [initialName, open]);

  useEffect(() => {
    if (pickedPoint) {
      setPoint(pickedPoint);
      onEndMapPick?.();
    }
  }, [onEndMapPick, pickedPoint]);

  const canSubmit = useMemo(
    () => name.trim().length >= 2 && point && (evidenceUrl.trim() || evidenceNote.trim().length >= 12),
    [evidenceNote, evidenceUrl, name, point]
  );

  if (!open || mapPicking) return null;

  async function submit(event) {
    event.preventDefault();
    if (!canSubmit || !backend) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await axios.post(
        `${backend}/api/campus/contributions`,
        {
          name: name.trim(),
          category,
          aliases: aliases.split(',').map((value) => value.trim()).filter(Boolean),
          description: description.trim(),
          lat: point.lat,
          lng: point.lng,
          evidenceUrl: evidenceUrl.trim(),
          evidenceNote: evidenceNote.trim(),
          sourceType: evidenceUrl.trim() ? 'map_link' : 'on_site',
          voterId: getCampusVoterId(),
        },
        { timeout: 12000 }
      );
      setSubmitted(response.data?.item);
      onSubmitted?.(response.data?.item);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Could not submit this location right now.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="contribution-overlay" role="dialog" aria-modal="true" aria-label="Add a campus location">
      <button className="contribution-backdrop" type="button" onClick={onClose} aria-label="Close" />
      <section className="contribution-sheet">
        <div className="contribution-handle" />
        <header>
          <div>
            <span>COMMUNITY CAMPUS MAP</span>
            <h3>{submitted ? 'Thank you — verification started' : 'Add a missing IITM place'}</h3>
            <p>{submitted ? 'Other campus users can now confirm or correct this point.' : 'Pin the real entrance, add evidence, and let the campus verify it.'}</p>
          </div>
          <button type="button" className="contribution-close" onClick={onClose}>×</button>
        </header>

        {submitted ? (
          <div className="contribution-success">
            <div className="contribution-success-mark">✓</div>
            <strong>{submitted.name}</strong>
            <p>1 confirmation recorded. It becomes a trusted navigation destination after enough independent confirmations, or an administrator verifies it.</p>
            <div className="contribution-vote-summary">
              <span><b>{submitted.verification?.confirmed || 1}</b> confirm</span>
              <span><b>{submitted.verification?.rejected || 0}</b> reject</span>
            </div>
            <button type="button" onClick={onClose}>Done</button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="contribution-grid">
              <label>
                <span>Place name</span>
                <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Example: NAC2 Room 611" maxLength={120} autoFocus />
              </label>
              <label>
                <span>Category</span>
                <select value={category} onChange={(event) => setCategory(event.target.value)}>
                  {CATEGORIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
            </div>

            <label>
              <span>Other names or abbreviations <small>optional, comma separated</small></span>
              <input value={aliases} onChange={(event) => setAliases(event.target.value)} placeholder="NAC 2, New Academic Complex II" maxLength={300} />
            </label>

            <label>
              <span>What is this place? <small>optional</small></span>
              <textarea value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Entrance is opposite the ED stop; lab is on the sixth floor." maxLength={500} rows={3} />
            </label>

            <div className="contribution-location-card">
              <div>
                <strong>Pin the public entrance</strong>
                <p>Use your current position while standing there, or tap the exact point on the campus map.</p>
              </div>
              <div className="contribution-location-actions">
                <button
                  type="button"
                  onClick={() => {
                    if (userLocation) setPoint({ lat: userLocation.lat, lng: userLocation.lng });
                    else setError('Current location is not available. Allow location or pin the point on the map.');
                  }}
                >Use my location</button>
                <button type="button" className="primary" onClick={onBeginMapPick}>Pin on map</button>
              </div>
              <PointPreview point={point} />
            </div>

            <label>
              <span>Ground-truth link <small>official page, map link or public directory</small></span>
              <input type="url" value={evidenceUrl} onChange={(event) => setEvidenceUrl(event.target.value)} placeholder="https://…" maxLength={500} />
            </label>
            <label>
              <span>On-site verification note</span>
              <textarea value={evidenceNote} onChange={(event) => setEvidenceNote(event.target.value)} placeholder="I am standing at the signed entrance; the board reads ‘…’." maxLength={500} rows={2} />
            </label>

            <div className="contribution-trust-note">
              <span>✓</span>
              <p>New places are not used for routing immediately. Independent votes and admin review prevent wrong pins and duplicate names.</p>
            </div>
            {error && <div className="contribution-error">{error}</div>}
            <button className="contribution-submit" type="submit" disabled={!canSubmit || submitting}>
              {submitting ? 'Submitting for verification…' : 'Submit for campus verification'}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
